import React, { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { faBirthdayCake } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { GridColumns } from "@mui/x-data-grid";

import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { TnAOverviewsFilter } from "../../../models/tna-overviews/tna-overview.filter";
import { TnACounterDTO } from "../../../models/tna/tna-counters/tna-counter-dto";
import ConcatenatedCell from "../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import Table from "../../../reusable-components/Table/Table";
import { TnAVacationTrackingsApiService } from "../../../services/tna-vacation-trackings/tna-vacation-trackings-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_MTPAL } from "../../../utils/constants/filter-tables-constants";
import { intlFormatDateShort } from "../../../utils/time-utils";
import { openTimesheetViewMode } from "../../../views/my-t-and-a/my-ee-tna-utils";
import QuickfiltersContainer from "../../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { MyTnAContext } from "../MyTeamPresenceAndLeaves";
import { EmployeeNameContainer } from "./styles";
import { EmployeeRef } from "../../../models/employees/employee-ref";
import { ColumnsHeaderTitle } from "../../../reusable-components/Table/styles";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { EmployeeGroupsApiService } from "../../../services/employee-groups/employee-groups-api.service";
import { RefModel } from "../../../models/base/ref-model";
import ExportVacationTrackingToExcel from "./ExportVacationTrackingToExcel/ExportVacationTrackingToExcel";

interface State {
  loading: boolean;
  error: string;
}

const TnAVacationTracking = () => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_vacation_tracker";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const theme = useTheme();

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
    searchingDate: { year, month, yearQuickFilter },
  } = useContext(MyTnAContext);

  const navigate = useNavigate();

  const allItems = useRef<TnACounterDTO[]>([]);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
  });

  const clickNavigate = (employee: EmployeeRef | null) => {
    if (!hasAccess && allowedToViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (hasAccess) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        },
      });
    }
  };

  const columns: GridColumns = [
    {
      field: "employee.employeeCode, employee.internalId",
      headerName: `${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "employee.name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span
                  title={row?.employee?.name}
                  onClick={() => {
                    clickNavigate(row.employee);
                  }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.legalEntity?.name}
              >
                {row?.legalEntity?.code ?? row?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      headerName: `${t(`common_words.employee`)} | ${t(`common_words.legal_entity`)}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "employee.tnAGroup",
      headerName: t(`common_words.tna_employee_group`),
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "counterType",
      headerName: t(`${translationFields}.type`),
      headerAlign: "center",
      valueGetter: ({ row }) => row.counterType?.name,
      flex: 1,
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "fromPrevYear",
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.from_previous_year`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.from_previous_year`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) => (!!row.fromPrevYear ? Number(row.fromPrevYear).toFixed(2) : ""),
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "generatedThisYear",
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.already_generated_this_year`)} ${year}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.already_generated_this_year`)} ${year}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) => (!!row.generatedThisYear ? Number(row.generatedThisYear).toFixed(2) : ""),
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "correctionsThisYear",
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.corrections`)} ${year}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.corrections`)} ${year}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) => (!!row.correctionsThisYear ? row.correctionsThisYear : ""),
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "usedOrPlanned",
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.already_used_or_planned`)} ${year}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.already_used_or_planned`)} ${year}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) => (!!row.usedOrPlanned ? Number(row.usedOrPlanned).toFixed(2) : ""),
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "balance",
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.total_balance`)} ${year}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.total_balance`)} ${year}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) => (!!row.balance ? Number(row.balance).toFixed(2) : ""),
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      sortable: true,
    },
  ];

  const defaultItem: Partial<TnACounterDTO> = {
    employee: null,
  };

  const loadEmployeeGroup = useCallback(async () => {
    try {
      const response = await new EmployeeGroupsApiService().loadItems({
        ids: employeeGroupOption?.employeeGroupIds,
      });

      if (!response.error) {
        return response.items[0];
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    }
  }, [employeeGroupOption?.employeeGroupIds]);

  const loadTnAVacationTrackingData = useCallback(
    async (pageSize: number = 10) => {
      try {
        const response = await new TnAVacationTrackingsApiService().loadItems(
          {
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            legalEntityIds: legalEntityOption?.legalEntityIds,
            onlyCurrentEmployees: !!onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
            year: year,
          } as TnAOverviewsFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize: pageSize,
          }
        );

        if (!response.error) {
          return response;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [
      employeeGroupOption?.employeeGroupIds,
      onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
      legalEntityOption?.legalEntityIds,
      year,
    ]
  );

  const quickfilters = useCallback(
    (items: RefModel[], loading: boolean) => {
      return (
        <QuickfiltersContainer>
          <>
            {!!legalEntityQuickFilter && legalEntityQuickFilter}
            {!!employeeGroupQuickFilter && employeeGroupQuickFilter}
            {!!onlyCurrentEmployeesQuickFilter && onlyCurrentEmployeesQuickFilter}
            {!!yearQuickFilter && yearQuickFilter}
            <ExportVacationTrackingToExcel
              loadEmployeeGroup={loadEmployeeGroup}
              loadTnAVacationTrackingData={loadTnAVacationTrackingData}
              disabled={state.loading || loading || !items?.length}
              allItems={() => allItems.current}
            />
          </>
        </QuickfiltersContainer>
      );
    },
    [state.loading, legalEntityQuickFilter, employeeGroupQuickFilter, onlyCurrentEmployeesQuickFilter]
  );

  return (
    <>
      {(hasAccess || allowedToViewOnly) && (
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <Table
            translationRootPath={tRP}
            columns={columns}
            serviceApi={new TnAVacationTrackingsApiService()}
            propsFilter={
              {
                ...legalEntityOption,
                ...employeeGroupOption,
                ...onlyCurrentEmployeesOptionValue,
                year,
              } as TnAOverviewsFilter
            }
            defaultItem={defaultItem}
            filter={FT_MTPAL}
            quickFilters={quickfilters}
            readOnly
            getAllItems={(items) => {
              allItems.current = items as TnACounterDTO[];
            }}
          />
        </LoadingOverlay>
      )}
    </>
  );
};

export default TnAVacationTracking;
